import React, {useMemo} from 'react';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useCustomClipboardCommands} from '../../../../../../components/LexicalEditor/hooks/useCustomClipboardCommands';
import {useCustomFontColorCommand} from '../../../../../../components/LexicalEditor/hooks/useCustomFontColorCommand';
import {useCustomFontFormatCommand} from '../../../../../../components/LexicalEditor/hooks/useCustomFontFormatCommand';
import {useCustomToolbarPlugin} from '../hooks/useCustomToolbarPlugin';
import {emailDialogID} from '../../../../testIDs';
import {useFormatList} from '../../../../../../components/LexicalEditor/hooks/useFormatList';
import {useEmailContext} from '../../../../context';
import {EmailWritingAssistantPlugin} from '../../../../../../components/LexicalEditor/plugins/EmailWritingAssistantPlugin';
import {BasicToolbar} from '../../../../../../components/LexicalEditor/components/Toolbar.parts/Toolbar';
import {Divider} from '../../../../../../components/LexicalEditor/components/Toolbar.parts/Divider';
import {supportAIBlock} from '../../../../../Automations/utils';

const toolbarId = 'email-editor-toolbar';

const styleConfig = {
    '--priority-toolbar-item-gaps': '.225em',
    '--priority-toolbar-border': 'none'
} as React.CSSProperties;

export const ToolbarPlugin = ({setIsDropdownOpen, showSuccessToast}) => {
    const {states, handlers} = useEmailContext();
    const {focusSubject, language} = states;
    const supportAI = useMemo(() => supportAIBlock(), []);

    const [editor] = useLexicalComposerContext();

    const {handlePaste, handleCopyAndCut} = useCustomClipboardCommands({
        editor,
        portalSelector: emailDialogID
    });
    const {lastSelectedColor, onFontColorSelect} = useCustomFontColorCommand({editor});
    const {fontFormat, handleFontSizeChange, setFontFormat} = useCustomFontFormatCommand({editor});
    const {isBold, isItalic, isUnderline, isStrikethrough} = fontFormat;
    const {formatBulletList, formatNumberedList, setBlockType} = useFormatList({editor});

    useCustomToolbarPlugin({editor, setFontFormat, setBlockType});

    return (
        <BasicToolbar
            style={styleConfig}
            toolbarId={toolbarId}
            onFontColorSelect={onFontColorSelect}
            isBold={isBold}
            isItalic={isItalic}
            isUnderline={isUnderline}
            isStrikethrough={isStrikethrough}
            formatBulletList={formatBulletList}
            formatNumberedList={formatNumberedList}
            lastSelectedColor={lastSelectedColor.current}
            handleFontSizeChange={handleFontSizeChange}
            handlePaste={handlePaste}
            handleCopyAndCut={handleCopyAndCut}
            showClipboardButtons
            disabled={focusSubject}
            emojiPickerPortalID={toolbarId}
        >
            <Divider />

            {supportAI ? (
                <EmailWritingAssistantPlugin
                    portalId={toolbarId}
                    showSuccessToast={showSuccessToast}
                    language={language === '3' ? 'en' : 'he'}
                    toggleAILoading={handlers.toggleAILoading}
                    disabled={focusSubject}
                />
            ) : null}
        </BasicToolbar>
    );
};
